// ============================================
// BLOG CONTENT LAZY LOADER
// Content'leri dinamik olarak yükler
// Performance optimization için kritik
// ============================================

import { BlogContent } from '../types';

/**
 * Blog içeriğini dinamik olarak yükler
 * @param slug - Blog slug'ı
 * @param category - Blog kategorisi
 * @returns Blog içeriği veya null
 * 
 * @example
 * // Standard category
 * loadBlogContent('google-ads-uzmani', 'Google Ads Danışmanlığı')
 * // -> loads from: content/google-ads-danismanligi/google-ads-uzmani.ts
 * 
 * // Category with special characters
 * loadBlogContent('harita-seo', 'Google Haritalar & Yerel SEO')
 * // -> loads from: content/google-haritalar-yerel-seo/harita-seo.ts
 * 
 * @testCases
 * Test scenarios to validate path transformation:
 * 1. "Google Ads Danışmanlığı" -> "google-ads-danismanligi"
 * 2. "Google Haritalar & Yerel SEO" -> "google-haritalar-yerel-seo"
 * 3. "Dijital Pazarlama & Genel" -> "dijital-pazarlama-genel"
 * 4. "AdWords Klasik" -> "adwords-klasik"
 * 5. Turkish chars: "İÖÜŞĞÇ" -> "iousgc"
 * 6. Multiple spaces: "Test  Category" -> "test-category"
 * 7. Edge case: "SEO/PPC Mix" -> "seoppc-mix"
 */
export async function loadBlogContent(
  slug: string,
  category: string
): Promise<BlogContent | null> {
  // Kategori adını dosya yolu formatına çevir - Geliştirilmiş Türkçe karakter dönüşümü
  const categoryPath = category
    .toLowerCase()
    // Önce Türkçe karakterleri dönüştür (normalize'dan önce)
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .replace(/ı/g, 'i')
    .replace(/Ğ/g, 'g')
    .replace(/ğ/g, 'g')
    .replace(/Ü/g, 'u')
    .replace(/ü/g, 'u')
    .replace(/Ş/g, 's')
    .replace(/ş/g, 's')
    .replace(/Ö/g, 'o')
    .replace(/ö/g, 'o')
    .replace(/Ç/g, 'c')
    .replace(/ç/g, 'c')
    // Diğer özel karakterleri temizle
    .replace(/&/g, '')
    .replace(/\s+/g, '-')
    // Normalize ile aksanları temizle (French, Spanish vb. için)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Sadece harf, rakam ve tire bırak
    .replace(/[^a-z0-9-]/g, '')
    // Birden fazla tire varsa tek tireye dönüştür
    .replace(/-+/g, '-')
    // Baş ve sondaki tireleri temizle
    .replace(/^-|-$/g, '');

  // Retry logic with exponential backoff
  const maxRetries = 3;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Dinamik import ile content'i yükle
      const contentModule = await import(
        `../content/${categoryPath}/${slug}.ts`
      );

      // Content'i return et
      const content = contentModule.default || contentModule[`${slug.replace(/-/g, '')}Content`];
      
      if (content) {
        return content;
      }
    } catch (error) {
      lastError = error as Error;
      
      // Son denemede fallback content dön
      if (attempt === maxRetries) {
        console.error(
          `❌ Blog content yüklenemedi (${maxRetries} deneme)`,
          `\n  Slug: ${slug}`,
          `\n  Kategori: ${category} -> ${categoryPath}`,
          `\n  Dosya: src/data/blog/content/${categoryPath}/${slug}.ts`,
          `\n  Hata:`, error
        );
        
        // Fallback content döndür
        return {
          tableOfContents: [],
          content: [{
            type: 'paragraph',
            value: 'İçerik yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.'
          }]
        };
      } else {
        // Exponential backoff: 250ms, 500ms, 1000ms
        await new Promise(resolve => setTimeout(resolve, 250 * Math.pow(2, attempt - 1)));
      }
    }
  }

  // Bu satıra hiç ulaşmamalı ama TypeScript için gerekli
  return null;
}

/**
 * Multiple blog content'lerini paralel olarak yükler
 * @param blogs - {slug, category} array
 * @returns Loaded content array
 */
export async function loadMultipleBlogContents(
  blogs: Array<{ slug: string; category: string }>
): Promise<Array<BlogContent | null>> {
  return Promise.all(
    blogs.map(blog => loadBlogContent(blog.slug, blog.category))
  );
}

/**
 * Preload blog content (hover veya route değişikliğinde)
 * @param slug - Blog slug
 * @param category - Blog category
 */
export function preloadBlogContent(slug: string, category: string): void {
  // Preload olarak import et, ama await etme
  loadBlogContent(slug, category).catch(() => {
    // Preload hatası sessizce yönetilir
  });
}
